import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Bot } from "lucide-react";
import { AppHeader } from "../../../components/app-header";
import { Button, buttonStyles } from "../../../components/ui/button";
import { EmptyState, InlineError, Skeleton } from "../../../components/ui/feedback";
import { notify } from "../../../components/ui/toast";
import { account } from "../../../copy/home";
import { type AiConnection, api, errorMessage } from "../../../lib/api";
import { useDelayedFlag } from "../../../lib/hooks";
import { useOnline } from "../../../lib/online";
import { aiConnectionsQuery, qk } from "../../../lib/queries";
import { relativeShort, shortDate } from "../../../lib/time";

export const Route = createFileRoute("/_authed/account/ai")({
  component: ConnectedAiPage,
});

/** Connected AI assistants (UX_SPEC §7). Disconnect is immediate, no undo. */
function ConnectedAiPage() {
  const client = useQueryClient();
  const online = useOnline();
  const connections = useQuery(aiConnectionsQuery);
  const showSkeleton = useDelayedFlag(connections.isPending, 150);
  const copy = account.ai;

  const disconnect = useMutation({
    mutationFn: (c: AiConnection) => api.disconnectAi(c.clientId),
    onMutate: (c) => {
      const previous = client.getQueryData<{ items: AiConnection[] }>(qk.aiConnections);
      client.setQueryData<{ items: AiConnection[] }>(qk.aiConnections, (old) => (old ? { items: old.items.filter((x) => x.clientId !== c.clientId) } : old));
      return { previous };
    },
    onSuccess: (_r, c) => notify.success(copy.disconnected(c.clientName)),
    onError: (e, _c, ctx) => {
      if (ctx?.previous) client.setQueryData(qk.aiConnections, ctx.previous);
      notify.error(errorMessage(e));
    },
  });

  const items = connections.data?.items ?? [];

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main id="main" className="mx-auto max-w-2xl px-4 pt-8 pb-16 sm:px-6">
        <h1 className="text-2xl font-semibold">{copy.pageTitle}</h1>
        <p className="mt-1 mb-6 text-sm text-muted-foreground">{copy.intro}</p>
        {connections.isPending ? (
          showSkeleton ? (
            <Skeleton className="h-16 w-full" />
          ) : null
        ) : connections.isError ? (
          <InlineError message={copy.loadFailed} onRetry={() => void connections.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Bot className="size-6" />}
            body={copy.empty}
            action={
              <Link to="/connect" className={buttonStyles({ variant: "primary" })}>
                {copy.connectOne}
              </Link>
            }
          />
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {items.map((c) => (
              <li key={c.clientId} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Bot className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">{c.clientName}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {c.clientHost} · {copy.row(shortDate(c.firstSeenAt), relativeShort(c.lastSeenAt))}
                  </span>
                </div>
                <Button size="sm" aria-label={copy.disconnectLabel(c.clientName)} disabled={!online || disconnect.isPending} onClick={() => disconnect.mutate(c)}>
                  {copy.disconnect}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
